import React, { useState, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import InputAdornment from "@material-ui/core/InputAdornment";
import EmailIcon from "@material-ui/icons/Email";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";
import CardFooter from "components/Card/CardFooter.js";
import CustomInput from "components/CustomInput/CustomInput.js";
import SimpleAlert from "../../components/AtutaAlerts/CustomAlerts";
import CircularProgress from "@material-ui/core/CircularProgress";
import AuthApi from "../../api_calls/AuthApi";

import styles from "assets/jss/material-dashboard-pro-react/views/loginPageStyle.js";

const useStyles = makeStyles(styles);

export default function ResetPassword() {
  const classes = useStyles();
  const [email, setEmail] = useState("");
  const [codeValues, setCodeValues] = useState(["", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [stage, setStage] = useState("email_input"); // email_input, reset_form
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const codeInputRefs = useRef([]);
  const history = useHistory();

  useEffect(() => {
    if (stage === "reset_form") {
      codeInputRefs.current[0]?.focus();
    }
  }, [stage]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setAlertMessage("");
    setErrors({});

    if (!email || !validateEmail(email)) {
      setErrors({ email: "Please provide a valid email address." });
      return;
    }

    setLoading(true);
    try {
      console.log("Sending password reset code to:", email);
      const response = await AuthApi.sendResetPasswordCode(email);
      if (response.status === "success") {
        setAlertMessage(
          "Verification code sent to your email. Please enter it below along with your new password."
        );
        setStage("reset_form");
      } else {
        setAlertMessage(
          response.message || "Failed to send code. Please try again."
        );
      }
    } catch (error) {
      console.error("Error sending password reset code:", error);
      setAlertMessage("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, e) => {
    const value = e.target.value.replace(/\D/g, "");
    if (!value && e.key !== "Backspace") return;

    const updated = [...codeValues];
    updated[index] = value.slice(0, 1);
    setCodeValues(updated);

    if (value && index < 3) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const updated = [...codeValues];
      if (codeValues[index]) {
        updated[index] = "";
        setCodeValues(updated);
      } else if (index > 0) {
        codeInputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < 3) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").replace(/\D/g, "");
    const updated = pasteData.split("").slice(0, 4);
    const combined = ["", "", "", ""];
    updated.forEach((char, i) => {
      combined[i] = char;
    });
    setCodeValues(combined);
    if (updated.length < 4) {
      codeInputRefs.current[updated.length]?.focus();
    } else {
      codeInputRefs.current[3]?.blur();
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setAlertMessage("");
    setErrors({});

    const code = codeValues.join("");
    if (code.length !== 4) {
      setErrors((prev) => ({
        ...prev,
        code: "Please enter the full 4-digit code.",
      }));
      return;
    }

    let formValid = true;
    if (!newPassword) {
      setErrors((prev) => ({
        ...prev,
        newPassword: "New password is required.",
      }));
      formValid = false;
    } else if (newPassword.length < 6) {
      setErrors((prev) => ({
        ...prev,
        newPassword: "Password must be at least 6 characters.",
      }));
      formValid = false;
    }
    if (newPassword !== confirmNewPassword) {
      setErrors((prev) => ({
        ...prev,
        confirmNewPassword: "Passwords do not match.",
      }));
      formValid = false;
    }

    if (!formValid) return;

    // --- Console Logs for Debugging ---
    console.log("Password Reset Code (entered by user):", code);
    console.log("New Password (entered by user):", newPassword);
    // ----------------------------------

    setLoading(true);
    try {
      const response = await AuthApi.resetPassword({
        email,
        resetCode: code,
        newPassword,
      });

      // --- Log the API response here ---
      console.log("API Reset Password Response:", response);
      // ----------------------------------

      if (response.status === "success") {
        setAlertMessage(
          "Your password has been reset successfully! Redirecting to login..."
        );
        setTimeout(() => {
          history.push("/auth/login-page"); // Redirect to login page after successful reset
        }, 2000);
      } else {
        // Updated to correctly access the nested message for errors
        setAlertMessage(
          response.data?.message ||
            "Failed to reset password. Please try again."
        );
      }
    } catch (error) {
      console.error("Error resetting password:", error);
      setAlertMessage("An error occurred during password reset.");
    } finally {
      setLoading(false);
    }
  };

  const getCardTitle = () => {
    if (stage === "email_input") return "Reset Your Password";
    if (stage === "reset_form") return "Enter Code and Set New Password";
    return "";
  };

  const getSubmitButtonText = () => {
    if (loading)
      return <CircularProgress size={24} style={{ color: "#fff" }} />;
    if (stage === "email_input") return "Send Code";
    if (stage === "reset_form") return "Reset Password";
    return "Submit";
  };

  const handleSubmitBasedOnStage = (e) => {
    e.preventDefault();
    if (stage === "email_input") {
      handleSendCode(e);
    } else if (stage === "reset_form") {
      handleResetPassword(e);
    }
  };

  return (
    <div className={classes.container}>
      <GridContainer justify="center">
        <GridItem xs={12} sm={6} md={6}>
          <form onSubmit={handleSubmitBasedOnStage}>
            <Card login>
              <CardHeader
                className={`${classes.cardHeader} ${classes.textCenter}`}
                color="rose"
              >
                <h4 className={classes.cardTitle}>{getCardTitle()}</h4>
              </CardHeader>
              <CardBody style={{ textAlign: "center" }}>
                {stage === "email_input" && (
                  <>
                    <p style={{ fontSize: "14px", marginBottom: "20px" }}>
                      Enter your email address to receive a password reset code.
                    </p>
                    <CustomInput
                      labelText="Email address..."
                      id="email"
                      formControlProps={{ fullWidth: true }}
                      inputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <EmailIcon className={classes.inputAdornmentIcon} />
                          </InputAdornment>
                        ),
                        type: "email",
                        value: email,
                        onChange: (e) => setEmail(e.target.value),
                        error: !!errors.email,
                      }}
                    />
                    {errors.email && (
                      <p
                        style={{
                          color: "red",
                          fontSize: "12px",
                          marginTop: "5px",
                        }}
                      >
                        {errors.email}
                      </p>
                    )}
                  </>
                )}

                {stage === "reset_form" && (
                  <>
                    <p style={{ fontSize: "14px", marginBottom: "12px" }}>
                      We’ve sent a 4-digit verification code to{" "}
                      <strong>{email}</strong>.
                      <br />
                      If you don’t see it,{" "}
                      <strong>check your spam or junk folder</strong>.
                    </p>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "12px",
                        marginBottom: "20px",
                      }}
                    >
                      {codeValues.map((value, index) => (
                        <input
                          key={index}
                          ref={(el) => (codeInputRefs.current[index] = el)}
                          value={value}
                          onChange={(e) => handleCodeChange(index, e)}
                          onKeyDown={(e) => handleCodeKeyDown(index, e)}
                          onPaste={handleCodePaste}
                          style={{
                            width: "50px",
                            height: "50px",
                            fontSize: "20px",
                            textAlign: "center",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                          }}
                          maxLength={1}
                          inputMode="numeric"
                        />
                      ))}
                    </div>
                    {errors.code && (
                      <p
                        style={{
                          color: "red",
                          fontSize: "12px",
                          marginTop: "-10px",
                          marginBottom: "15px",
                        }}
                      >
                        {errors.code}
                      </p>
                    )}

                    <p style={{ fontSize: "14px", marginBottom: "20px" }}>
                      Please enter your new password.
                    </p>
                    <CustomInput
                      labelText="New Password..."
                      id="new-password"
                      formControlProps={{ fullWidth: true }}
                      inputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <span
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                setShowNewPassword(!showNewPassword)
                              }
                            >
                              {showNewPassword ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </span>
                          </InputAdornment>
                        ),
                        type: showNewPassword ? "text" : "password",
                        autoComplete: "new-password",
                        value: newPassword,
                        onChange: (e) => setNewPassword(e.target.value),
                        error: !!errors.newPassword,
                      }}
                    />
                    {errors.newPassword && (
                      <p
                        style={{
                          color: "red",
                          fontSize: "12px",
                          marginTop: "5px",
                        }}
                      >
                        {errors.newPassword}
                      </p>
                    )}
                    <CustomInput
                      labelText="Confirm New Password..."
                      id="confirm-new-password"
                      formControlProps={{ fullWidth: true }}
                      inputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <span
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                setShowConfirmNewPassword(
                                  !showConfirmNewPassword
                                )
                              }
                            >
                              {showConfirmNewPassword ? (
                                <Visibility />
                              ) : (
                                <VisibilityOff />
                              )}
                            </span>
                          </InputAdornment>
                        ),
                        type: showConfirmNewPassword ? "text" : "password",
                        autoComplete: "new-password",
                        value: confirmNewPassword,
                        onChange: (e) => setConfirmNewPassword(e.target.value),
                        error: !!errors.confirmNewPassword,
                      }}
                    />
                    {errors.confirmNewPassword && (
                      <p
                        style={{
                          color: "red",
                          fontSize: "12px",
                          marginTop: "5px",
                        }}
                      >
                        {errors.confirmNewPassword}
                      </p>
                    )}
                  </>
                )}
              </CardBody>
              <CardFooter className={classes.justifyContentCenter}>
                <Button color="rose" type="submit" disabled={loading}>
                  {getSubmitButtonText()}
                </Button>
              </CardFooter>
            </Card>
            {alertMessage && (
              <SimpleAlert
                message={alertMessage}
                onClose={() => setAlertMessage("")}
              />
            )}
          </form>
        </GridItem>
      </GridContainer>
    </div>
  );
}
